import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronRight, MapPin, CheckCircle2, FileText, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import Header from "@/components/Header";
import MobileStickyBar from "@/components/MobileStickyBar";
import Footer from "@/components/Footer";
import { WHATSAPP_LINK } from "@/constants/business";
import { useDialogs } from "@/context/DialogsContext";

/**
 * Reusable premium layout for every programmatic SEO page (city, route, service, region).
 * Keeps a consistent, high-quality structure so pages never look like generic thin content.
 */
export default function SEOPageLayout({
  breadcrumbItems = [],
  eyebrow,
  h1,
  intro,
  serviceLinks = [],
  relatedGroups = [],
  trustItems = [],
  faqItems = [],
  ctaProfile = null,
  testIdPrefix = "seo-page",
}) {
  const { openQuote } = useDialogs();

  return (
    <div className="pb-16 md:pb-0" data-testid={`${testIdPrefix}-container`}>
      <Header />
      <MobileStickyBar />

      {/* Banner */}
      <section className="relative bg-navy overflow-hidden pt-28 pb-16 md:pt-36 md:pb-20 px-4 md:px-8">
        <svg className="absolute inset-0 w-full h-full opacity-15" preserveAspectRatio="none">
          <line x1="0" y1="20%" x2="100%" y2="8%" stroke="#F95738" strokeWidth="1.5" className="dash-line" />
          <line x1="0" y1="85%" x2="100%" y2="70%" stroke="#C48B5F" strokeWidth="1.5" className="dash-line" />
        </svg>
        <div className="relative max-w-5xl mx-auto">
          <nav aria-label="Fil d'ariane" className="flex flex-wrap items-center gap-1.5 text-xs text-white/50 mb-6" data-testid={`${testIdPrefix}-breadcrumb`}>
            {breadcrumbItems.map((item, i) => (
              <span key={item.href || item.label} className="flex items-center gap-1.5">
                {i > 0 && <ChevronRight className="w-3 h-3" />}
                {item.href ? (
                  <Link to={item.href} className="hover:text-white transition-colors">
                    {item.label}
                  </Link>
                ) : (
                  <span className="text-white/80">{item.label}</span>
                )}
              </span>
            ))}
          </nav>
          {eyebrow && (
            <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-orange mb-4">
              {eyebrow}
            </span>
          )}
          <h1 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl leading-[1.1] tracking-tight text-white max-w-3xl mb-5">
            {h1}
          </h1>
          <p className="text-white/70 text-base leading-relaxed max-w-2xl mb-8">{intro}</p>
          <div className="flex flex-wrap gap-3">
            <Button
              data-testid={`${testIdPrefix}-cta-quote`}
              onClick={() => openQuote(ctaProfile)}
              className="bg-orange hover:bg-orange-hover text-white rounded-full px-7 py-6 font-semibold transition-transform hover:-translate-y-1 shadow-lg shadow-orange/30"
            >
              <FileText className="w-4 h-4 mr-2" /> Demander un devis
            </Button>
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`${testIdPrefix}-cta-whatsapp`}
              className="inline-flex items-center bg-whatsapp text-white rounded-full px-7 py-3 font-semibold transition-transform hover:-translate-y-1 shadow-lg shadow-green-500/30"
            >
              <MessageCircle className="w-4 h-4 mr-2" /> WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* Services grid relevant to this page */}
      {serviceLinks.length > 0 && (
        <section className="py-16 md:py-20 px-4 md:px-8 max-w-6xl mx-auto" data-testid={`${testIdPrefix}-services-section`}>
          <h2 className="font-heading font-bold text-2xl md:text-3xl tracking-tight text-navy mb-8">
            Nos prestations concernées
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {serviceLinks.map((s) => (
              <Link
                key={s.href}
                to={s.href}
                data-testid={`${testIdPrefix}-service-link-${s.href}`}
                className="bg-white border border-gray-200 rounded-xl p-5 hover:border-orange hover:shadow-md transition-all"
              >
                <h3 className="font-heading font-semibold text-sm text-navy mb-1.5">{s.label}</h3>
                {s.text && <p className="text-xs text-muted-foreground leading-relaxed">{s.text}</p>}
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Trust markers */}
      {trustItems.length > 0 && (
        <section className="py-14 px-4 md:px-8 bg-graylight" data-testid={`${testIdPrefix}-trust-section`}>
          <div className="max-w-6xl mx-auto grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {trustItems.map((t, i) => (
              <div key={i} className="flex items-center gap-3 bg-white border border-gray-200 rounded-xl px-4 py-3.5">
                <CheckCircle2 className="w-4 h-4 text-orange shrink-0" />
                <span className="text-sm font-medium text-navy">{t}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Related links (nearby cities / other routes / other services) */}
      {relatedGroups.map((group) => (
        <section key={group.title} className="py-14 px-4 md:px-8 max-w-6xl mx-auto" data-testid={`${testIdPrefix}-related-${group.title}`}>
          <h2 className="font-heading font-bold text-xl md:text-2xl tracking-tight text-navy mb-6">{group.title}</h2>
          <div className="flex flex-wrap gap-2.5">
            {group.items.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className="flex items-center gap-1.5 bg-white border border-gray-200 rounded-full px-4 py-2 text-sm font-medium text-navy hover:border-orange hover:text-orange transition-colors"
              >
                <MapPin className="w-3.5 h-3.5 text-orange" /> {item.label}
              </Link>
            ))}
          </div>
        </section>
      ))}

      {/* FAQ */}
      {faqItems.length > 0 && (
        <section className="py-16 md:py-20 px-4 md:px-8 max-w-3xl mx-auto" data-testid={`${testIdPrefix}-faq-section`}>
          <h2 className="font-heading font-bold text-2xl md:text-3xl tracking-tight text-navy mb-8">
            Questions fréquentes
          </h2>
          <Accordion type="single" collapsible className="space-y-3">
            {faqItems.map((item, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                className="bg-white border border-gray-200 rounded-xl px-5"
              >
                <AccordionTrigger className="text-left font-heading font-semibold text-navy text-sm hover:no-underline">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-sm leading-relaxed">{item.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>
      )}

      {/* Final CTA */}
      <section className="py-16 md:py-20 px-4 md:px-8 max-w-6xl mx-auto" data-testid={`${testIdPrefix}-final-cta`}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="bg-navy rounded-3xl px-6 md:px-16 py-14 text-center"
        >
          <h2 className="font-heading font-bold text-2xl md:text-4xl tracking-tight text-white mb-4">
            Obtenez votre devis en quelques minutes
          </h2>
          <p className="text-white/70 max-w-lg mx-auto text-sm md:text-base mb-8">
            Décrivez votre besoin, recevez un tarif clair et confirmez votre créneau avec Express Delivery.
          </p>
          <Button
            data-testid={`${testIdPrefix}-final-cta-button`}
            onClick={() => openQuote(ctaProfile)}
            className="bg-orange hover:bg-orange-hover text-white rounded-full px-8 py-6 font-semibold transition-transform hover:-translate-y-1 shadow-lg shadow-orange/30"
          >
            Demander un devis
          </Button>
        </motion.div>
      </section>

      <Footer />
    </div>
  );
}
